import { createFileRoute, Link } from "@tanstack/react-router";
import { useContext, useMemo, useState } from "react";
import { Dialog, Modal as RACModal, ModalOverlay } from "react-aria-components";
import {
  AppError,
  Button,
  EmptyState,
  Loader,
  TextInput,
  toast,
} from "@bfc-software/pallet";
import { TFunction } from "i18next";
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Edit2,
  X,
} from "react-feather";
import { AuthContext, AuthUtils, logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient as issApiClient } from "@bfc-software/bfc-web-ui/lib/iss-api-client";
import { apiClient as aaaApiClient } from "@bfc-software/bfc-web-ui/lib/aaa-api-client";
import {
  apiClient as orgCfgApiClient,
  rawFetchClient as orgCfgFetchClient,
} from "@bfc-software/bfc-web-ui/lib/orgcfg-api-client";
import palletOverrides from "@bfc-software/bfc-web-ui/pallet-overrides.module.css";
import modalStyles from "@bfc-software/pallet/components/structure/modal.module.css";

import styles from "./user-groups.module.css";

/**
 * Same scope as selectors.tsx's `APP_NAME` -- app users (and now app user
 * groups) are shared across every app on a warehouse, and this is what
 * scopes both to the ones that sign in on the floor.
 */
const APP_NAME = "select";

/**
 * The `group` key of the `selectorSettings` enum (org_config.yaml) --
 * `appUserGroups.users` is this feature's own membership list and the
 * authoritative source of truth, but selectors.tsx's table/detail view still
 * reads a selector's group from this per-user settings key, so every
 * membership change here is mirrored onto it.
 */
const SETTINGS_GROUP_KEY = "group";

/**
 * Mirrors a group-membership change onto the selector's own settings record
 * so selectors.tsx's "User group" column stays truthful. Fetches the
 * selector's current settings first (an update on a selector with no
 * settings record yet 404s, so a missing record falls back to `add` instead)
 * and merges the group key in rather than overwriting the whole map, since
 * the other selectorSettings keys (task type eligibility, validation level,
 * etc.) are unrelated to group membership.
 */
async function syncSelectorGroupSetting(
  orgId: string,
  warehouseId: string,
  userId: string,
  groupName: string | null,
): Promise<void> {
  const { data, response } = await orgCfgFetchClient().GET(
    "/orgs/user_settings/get",
    { params: { query: { orgId, warehouseId, userId } } },
  );
  const exists = response.status !== 404;
  const nextSettings: Record<string, string> = exists
    ? { ...data?.settings }
    : {};
  if (groupName) {
    nextSettings[SETTINGS_GROUP_KEY] = groupName;
  } else {
    delete nextSettings[SETTINGS_GROUP_KEY];
  }

  const { error } = await orgCfgFetchClient().POST(
    exists ? "/orgs/user_settings/update" : "/orgs/user_settings/add",
    { body: { orgId, warehouseId, userId, settings: nextSettings } },
  );
  if (error) throw error;
}

const AVATAR_COLORS = [
  "#2f6fb2",
  "#c47f12",
  "#0aa3a0",
  "#7b61c9",
  "#cf2b22",
  "#3d8b3d",
];

/** Mirrors zones.tsx's `ZONE_COLORS` -- cycled by the zone's own id (not its name) so a renamed zone keeps its color, and so a zone's chip color matches the Zones page's own card border for the same zone. */
const ZONE_COLORS = [
  "#2f6fb2",
  "#c47f12",
  "#0aa3a0",
  "#7b61c9",
  "#cf2b22",
  "#3d8b3d",
];

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/user-groups",
)({
  component: RouteComponent,
});

type Zone = {
  id: number;
  name: string;
};

type AppUser = {
  id: number;
  user_id: string;
  user_name?: string;
  active: boolean;
};

type AppUserGroup = {
  id?: number;
  orgId: string;
  warehouseId: string;
  appName: string;
  name: string;
  zones: string[];
  users: string[];
};

function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0]![0]! + words[1]![0]!).toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase();
}

function avatarColorOf(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash += userId.charCodeAt(i);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]!;
}

/** AAA's `user_name` is optional -- a selector created before it existed, or without one set, falls back to the login id. */
function displayNameOf(user: AppUser): string {
  return user.user_name || user.user_id;
}

/**
 * openapi-react-query throws the parsed apiResponse body itself
 * (`{ code, message }`) rather than an `Error` -- see the analogous helper
 * in selectors.tsx and zones.tsx.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown; error?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;
  const next = items.slice();
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item!);
  return next;
}

function ZonePriorityBadge({ position }: { position: number }) {
  return <span className={styles.priorityBadge}>{position}</span>;
}

function ZoneChip({ name, color }: { name: string; color: string }) {
  return (
    <span
      className={styles.zoneChip}
      style={{ color, backgroundColor: `${color}1e` }}
    >
      {name}
    </span>
  );
}

function SelectorAvatar({ user }: { user: AppUser }) {
  return (
    <span
      className={styles.avatar}
      style={{ backgroundColor: avatarColorOf(user.user_id) }}
    >
      {initialsOf(displayNameOf(user))}
    </span>
  );
}

/**
 * The create-group form -- an inline panel injected under the "Create user
 * group" button rather than a modal, matching zones.tsx's CreateZonePanel
 * (picking zones and selectors stays in the same view as the rest of the
 * group list).
 */
function CreateGroupPanel({
  allZoneNames,
  zoneColor,
  unassignedSelectors,
  onSubmit,
  onClose,
  t,
}: {
  allZoneNames: string[];
  zoneColor: (name: string) => string;
  unassignedSelectors: AppUser[];
  onSubmit: (values: {
    name: string;
    zones: string[];
    users: string[];
  }) => Promise<void>;
  onClose: () => void;
  t: TFunction;
}) {
  const [name, setName] = useState("");
  const [zones, setZones] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleZone = (zoneName: string) => {
    setZones((prev) =>
      prev.includes(zoneName)
        ? prev.filter((z) => z !== zoneName)
        : [...prev, zoneName],
    );
  };

  const toggleSelector = (userId: string) => {
    setUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const canSubmit = name.trim().length > 0 && zones.length > 0;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || !canSubmit) return;

    setIsSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), zones, users: userIds });
      onClose();
    } catch {
      // onSubmit already reported the failure via toast; keep the panel open
      // so the admin can retry without re-entering values.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className={styles.panel}
      data-test-role="create-group-panel"
    >
      <div className={styles.panelTitle}>
        {t("userGroups.form.createTitle")}
      </div>

      <TextInput
        name="name"
        label={t("userGroups.form.nameLabel")}
        placeholder={t("userGroups.form.namePlaceholder")}
        value={name}
        onChange={(ev) => setName(ev.target.value)}
        autofocus={true}
        isRequired={true}
      />

      <div>
        <span className={styles.fieldLabel}>
          {t("userGroups.form.zonesLabel")}
        </span>
        <div className={styles.pillRow}>
          {allZoneNames.map((zoneName) => {
            const selected = zones.includes(zoneName);
            return (
              <button
                type="button"
                key={zoneName}
                className={selected ? styles.pillSelected : styles.pill}
                aria-pressed={selected}
                onClick={() => toggleZone(zoneName)}
              >
                <span className={styles.pillCheck}>
                  {selected ? <Check size={11} strokeWidth={3} /> : null}
                </span>
                {zoneName}
              </button>
            );
          })}
        </div>
      </div>

      {zones.length > 0 ? (
        <div className={styles.priorityPanel}>
          <span className={styles.fieldLabel}>
            {t("userGroups.form.priorityHeading")}
          </span>
          <p className={styles.priorityPanelIntro}>
            {t("userGroups.form.priorityIntro")}
          </p>
          {zones.map((zoneName, index) => (
            <div key={zoneName} className={styles.priorityListRow}>
              <div className={styles.priorityRow}>
                <ZonePriorityBadge position={index + 1} />
                <ZoneChip name={zoneName} color={zoneColor(zoneName)} />
              </div>
              <div className={styles.reorderButtons}>
                <button
                  type="button"
                  className={styles.reorderButton}
                  aria-label={t("userGroups.rowActions.moveUp")}
                  disabled={index === 0}
                  onClick={() => setZones((prev) => moveItem(prev, index, -1))}
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  className={styles.reorderButton}
                  aria-label={t("userGroups.rowActions.moveDown")}
                  disabled={index === zones.length - 1}
                  onClick={() => setZones((prev) => moveItem(prev, index, 1))}
                >
                  <ChevronDown size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div>
        <span className={styles.fieldLabel}>
          {t("userGroups.form.selectorsLabel")}
        </span>
        <p className={styles.fieldNote}>
          {userIds.length > 0
            ? t("userGroups.form.selectorsJoining", { count: userIds.length })
            : t("userGroups.form.selectorsOptional")}
        </p>
        <div className={styles.selectorChipRow}>
          {unassignedSelectors.map((user) => {
            const selected = userIds.includes(user.user_id);
            return (
              <button
                type="button"
                key={user.user_id}
                className={
                  selected ? styles.selectorChipSelected : styles.selectorChip
                }
                aria-pressed={selected}
                onClick={() => toggleSelector(user.user_id)}
              >
                <span
                  className={styles.chipAvatar}
                  style={{ backgroundColor: avatarColorOf(user.user_id) }}
                >
                  {initialsOf(displayNameOf(user))}
                </span>
                {displayNameOf(user)}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.panelActions}>
        <Button
          type="submit"
          className={styles.createButton}
          text={
            isSubmitting
              ? t("userGroups.form.saving")
              : t("userGroups.form.submit")
          }
          isDisabled={isSubmitting || !canSubmit}
        />
        <Button
          type="button"
          variant="tertiary"
          text={t("userGroups.form.cancel")}
          onPress={onClose}
          isDisabled={isSubmitting}
        />
      </div>
    </form>
  );
}

/**
 * A group's read-only expansion -- its current selectors, each removable
 * with a single click. Opened via the row's caret.
 */
function GroupSelectorList({
  group,
  selectorById,
  onRemoveSelector,
  t,
}: {
  group: AppUserGroup;
  selectorById: Map<string, AppUser>;
  onRemoveSelector: (userId: string) => void;
  t: TFunction;
}) {
  if (group.users.length === 0) {
    return (
      <p className={styles.emptyNote}>{t("userGroups.edit.noSelectors")}</p>
    );
  }

  return (
    <div className={styles.selectorList}>
      {group.users.map((userId) => {
        const user = selectorById.get(userId);
        return (
          <div key={userId} className={styles.selectorListRow}>
            {user ? <SelectorAvatar user={user} /> : null}
            <span className={styles.selectorName}>
              {user ? displayNameOf(user) : userId}
            </span>
            <span className={styles.selectorZones}>
              {group.zones.join(", ")}
            </span>
            <span className={styles.selectorRowSpacer} />
            <Button
              variant="tertiary"
              text={t("userGroups.rowActions.removeSelector")}
              onPress={() => onRemoveSelector(userId)}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * The full edit surface for an existing group -- direct manipulation of its
 * zone priority order and its selector membership, each change saved
 * immediately (no separate "save" step, matching zones.tsx's product-type
 * editing).
 */
function GroupEditPanel({
  group,
  allZoneNames,
  zoneColor,
  unassignedSelectors,
  selectorById,
  onChangeZones,
  onAddSelector,
  onRemoveSelector,
  t,
}: {
  group: AppUserGroup;
  allZoneNames: string[];
  zoneColor: (name: string) => string;
  unassignedSelectors: AppUser[];
  selectorById: Map<string, AppUser>;
  onChangeZones: (zones: string[]) => void;
  onAddSelector: (userId: string) => void;
  onRemoveSelector: (userId: string) => void;
  t: TFunction;
}) {
  const addableZones = allZoneNames.filter((z) => !group.zones.includes(z));

  return (
    <div className={styles.editPanel}>
      <div className={styles.editColumn}>
        <span className={styles.editColumnHeading}>
          {t("userGroups.edit.zonesHeading")}
        </span>
        <p className={styles.editColumnIntro}>
          {t("userGroups.edit.zonesIntro")}
        </p>

        <div className={styles.editList}>
          {group.zones.map((zoneName, index) => (
            <div key={zoneName} className={styles.editRow}>
              <div className={styles.editRowMain}>
                <ZonePriorityBadge position={index + 1} />
                <ZoneChip name={zoneName} color={zoneColor(zoneName)} />
              </div>
              <div className={styles.editRowActions}>
                <button
                  type="button"
                  className={styles.reorderButton}
                  aria-label={t("userGroups.rowActions.moveUp")}
                  disabled={index === 0}
                  onClick={() =>
                    onChangeZones(moveItem(group.zones, index, -1))
                  }
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  className={styles.reorderButton}
                  aria-label={t("userGroups.rowActions.moveDown")}
                  disabled={index === group.zones.length - 1}
                  onClick={() => onChangeZones(moveItem(group.zones, index, 1))}
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  type="button"
                  className={styles.removeButton}
                  aria-label={t("userGroups.rowActions.removeZone")}
                  onClick={() =>
                    onChangeZones(group.zones.filter((z) => z !== zoneName))
                  }
                >
                  <X size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.addSection}>
          <span className={styles.addSectionLabel}>
            {t("userGroups.edit.addZone")}
          </span>
          <div className={styles.addChipRow}>
            {addableZones.map((zoneName) => (
              <button
                type="button"
                key={zoneName}
                className={styles.addChip}
                onClick={() => onChangeZones([...group.zones, zoneName])}
              >
                +{zoneName}
              </button>
            ))}
            {addableZones.length === 0 ? (
              <p className={styles.addChipEmpty}>
                {t("userGroups.edit.noZonesLeft")}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className={styles.editColumn}>
        <span className={styles.editColumnHeading}>
          {t("userGroups.edit.selectorsHeading")}
        </span>
        <p className={styles.editColumnIntro}>
          {t("userGroups.selectorsAssigned", { count: group.users.length })}
        </p>

        <div className={styles.editList}>
          {group.users.map((userId) => {
            const user = selectorById.get(userId);
            return (
              <div key={userId} className={styles.editRow}>
                <div className={styles.editRowMain}>
                  {user ? <SelectorAvatar user={user} /> : null}
                  <span className={styles.editRowName}>
                    {user ? displayNameOf(user) : userId}
                  </span>
                </div>
                <div className={styles.editRowActions}>
                  <button
                    type="button"
                    className={styles.removeButton}
                    aria-label={t("userGroups.rowActions.removeSelector")}
                    onClick={() => onRemoveSelector(userId)}
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.addSection}>
          <span className={styles.addSectionLabel}>
            {t("userGroups.edit.addSelector")}
          </span>
          <p className={styles.fieldNote}>
            {t("userGroups.edit.addSelectorIntro")}
          </p>
          <div className={styles.addChipRow}>
            {unassignedSelectors.map((user) => (
              <button
                type="button"
                key={user.user_id}
                className={styles.addChip}
                onClick={() => onAddSelector(user.user_id)}
              >
                +{displayNameOf(user)}
              </button>
            ))}
            {unassignedSelectors.length === 0 ? (
              <p className={styles.addChipEmpty}>
                {t("userGroups.edit.noSelectorsLeft")}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function GroupRow({
  group,
  allZoneNames,
  zoneColor,
  selectorById,
  unassignedSelectors,
  isOpen,
  mode,
  onToggleView,
  onToggleEdit,
  onChangeZones,
  onAddSelector,
  onRemoveSelector,
  onDelete,
  t,
}: {
  group: AppUserGroup;
  allZoneNames: string[];
  zoneColor: (name: string) => string;
  selectorById: Map<string, AppUser>;
  unassignedSelectors: AppUser[];
  isOpen: boolean;
  mode: "view" | "edit";
  onToggleView: () => void;
  onToggleEdit: () => void;
  onChangeZones: (zones: string[]) => void;
  onAddSelector: (userId: string) => void;
  onRemoveSelector: (userId: string) => void;
  onDelete: () => void;
  t: TFunction;
}) {
  return (
    <div className={styles.groupCard} data-test-role="user-group-row">
      <div className={styles.groupHeader}>
        <button
          type="button"
          className={styles.caretButton}
          aria-label={t(
            isOpen
              ? "userGroups.rowActions.collapse"
              : "userGroups.rowActions.expand",
          )}
          aria-expanded={isOpen}
          onClick={onToggleView}
        >
          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className={styles.groupNameCol}>
          <span className={styles.groupName} data-test-role="user-group-name">
            {group.name}
          </span>
          <span className={styles.groupCount}>
            {t("userGroups.selectorsAssigned", { count: group.users.length })}
          </span>
        </div>

        <div className={styles.groupZonesCol}>
          <span className={styles.zonesCaption}>
            {t("userGroups.zonesByPriority")}
          </span>
          <div className={styles.priorityRow}>
            {group.zones.map((zoneName, index) => (
              <span key={zoneName} className={styles.priorityRow}>
                <ZonePriorityBadge position={index + 1} />
                <ZoneChip name={zoneName} color={zoneColor(zoneName)} />
              </span>
            ))}
          </div>
        </div>

        <div className={styles.groupActions}>
          <button
            type="button"
            className={`${styles.iconButton} ${isOpen && mode === "edit" ? styles.iconButtonActive : ""}`}
            aria-label={t(
              isOpen && mode === "edit"
                ? "userGroups.rowActions.doneEditing"
                : "userGroups.rowActions.edit",
            )}
            onClick={onToggleEdit}
            data-test-role="user-group-edit"
          >
            {isOpen && mode === "edit" ? (
              <Check size={15} />
            ) : (
              <Edit2 size={14} />
            )}
          </button>
          <Button
            variant="tertiary"
            text={t("userGroups.rowActions.delete")}
            isDisabled={group.users.length > 0}
            onPress={onDelete}
          />
        </div>
      </div>

      {isOpen && mode === "view" ? (
        <GroupSelectorList
          group={group}
          selectorById={selectorById}
          onRemoveSelector={onRemoveSelector}
          t={t}
        />
      ) : null}

      {isOpen && mode === "edit" ? (
        <GroupEditPanel
          group={group}
          allZoneNames={allZoneNames}
          zoneColor={zoneColor}
          unassignedSelectors={unassignedSelectors}
          selectorById={selectorById}
          onChangeZones={onChangeZones}
          onAddSelector={onAddSelector}
          onRemoveSelector={onRemoveSelector}
          t={t}
        />
      ) : null}
    </div>
  );
}

function DeleteGroupConfirm({
  group,
  isPending,
  onCancel,
  onConfirm,
  t,
}: {
  group: AppUserGroup | undefined;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  t: TFunction;
}) {
  return (
    <ModalOverlay
      className={modalStyles.modalOverlay}
      isDismissable={!isPending}
      isOpen={group !== undefined}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <RACModal className={modalStyles.modal}>
        <Dialog
          className={modalStyles.dialog}
          data-test-role="delete-group-confirm"
        >
          <div className={styles.deleteConfirm}>
            <h2>{t("userGroups.deleteConfirm.title")}</h2>
            <p className={styles.deleteConfirmBody}>
              {t("userGroups.deleteConfirm.body")}
            </p>
            {group ? (
              <p className={styles.deleteConfirmTarget}>{group.name}</p>
            ) : null}
          </div>
          <div className={styles.modalActions}>
            <Button
              text={t("userGroups.deleteConfirm.cancel")}
              variant="tertiary"
              onPress={onCancel}
              isDisabled={isPending}
            />
            <Button
              text={t("userGroups.deleteConfirm.confirm")}
              variant="danger"
              onPress={onConfirm}
              isDisabled={isPending}
            />
          </div>
        </Dialog>
      </RACModal>
    </ModalOverlay>
  );
}

export function RouteComponent() {
  const params = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const issClient = useMemo(() => issApiClient(), []);
  const aaaClient = useMemo(() => aaaApiClient(), []);
  const orgCfgClient = useMemo(() => orgCfgApiClient(), []);

  const orgId = AuthUtils.getOrgIdByStub(authState, params.org) ?? params.org;
  const warehouseId = AuthUtils.getWarehouseId(
    authState,
    orgId,
    params.location,
  );
  const idsReady = typeof warehouseId === "string";

  const zonesQuery = issClient.useQuery("get", "/zones/all", {});
  const selectorsQuery = aaaClient.useQuery(
    "get",
    "/app_user/list",
    {
      params: {
        query: {
          org_id: orgId,
          warehouse_id: warehouseId ?? "",
          app_name: APP_NAME,
        },
      },
    },
    { enabled: idsReady },
  );
  const groupsQuery = orgCfgClient.useQuery(
    "get",
    "/orgs/user_groups/list",
    {
      params: {
        query: {
          orgId,
          warehouseId: warehouseId ?? "",
          appName: APP_NAME,
        },
      },
    },
    { enabled: idsReady },
  );

  const addMutation = orgCfgClient.useMutation("post", "/orgs/user_groups/add");
  const updateMutation = orgCfgClient.useMutation(
    "post",
    "/orgs/user_groups/update",
  );
  const removeMutation = orgCfgClient.useMutation(
    "post",
    "/orgs/user_groups/remove",
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [openName, setOpenName] = useState<string | undefined>(undefined);
  const [openMode, setOpenMode] = useState<"view" | "edit">("view");
  const [deleteTarget, setDeleteTarget] = useState<AppUserGroup | undefined>(
    undefined,
  );

  const zones = (zonesQuery.data?.data ?? []) as Zone[];
  const allZoneNames = [...zones]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((zone) => zone.name);
  const zoneColorByName = new Map(
    zones.map((zone) => [
      zone.name,
      ZONE_COLORS[zone.id % ZONE_COLORS.length]!,
    ]),
  );
  const zoneColor = (name: string) => zoneColorByName.get(name) ?? "#6b7280";

  const selectors = (selectorsQuery.data ?? []) as AppUser[];
  const selectorById = new Map(selectors.map((user) => [user.user_id, user]));

  const groups = ((groupsQuery.data ?? []) as AppUserGroup[])
    .slice()
    .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

  const assignedUserIds = new Set(groups.flatMap((group) => group.users));
  const unassignedSelectors = selectors.filter(
    (user) => !assignedUserIds.has(user.user_id),
  );

  const isLoading =
    !idsReady ||
    zonesQuery.isLoading ||
    selectorsQuery.isLoading ||
    groupsQuery.isLoading;
  const isError =
    zonesQuery.isError || selectorsQuery.isError || groupsQuery.isError;

  const refetchGroups = () => {
    groupsQuery.refetch();
  };

  const closeOpen = () => setOpenName(undefined);

  const toggleView = (name: string) => {
    if (openName === name && openMode === "view") {
      closeOpen();
    } else {
      setOpenName(name);
      setOpenMode("view");
    }
  };

  const toggleEdit = (name: string) => {
    if (openName === name && openMode === "edit") {
      closeOpen();
    } else {
      setOpenName(name);
      setOpenMode("edit");
    }
  };

  /**
   * Best-effort: the group's own `users` array is already the membership
   * source of truth by this point, so a failure here (an admin can still fix
   * it from the Selectors page) is warned rather than rolled back.
   */
  const syncSelectorGroup = async (
    userId: string,
    groupName: string | null,
  ) => {
    try {
      await syncSelectorGroupSetting(orgId, warehouseId!, userId, groupName);
    } catch (error) {
      logger.warn("Failed to sync selector's group setting", {
        error,
        userId,
        groupName,
      });
      toast.error(t("userGroups.edit.settingsSyncFailed", { userId }));
    }
  };

  const handleCreateSubmit = async (values: {
    name: string;
    zones: string[];
    users: string[];
  }) => {
    try {
      await addMutation.mutateAsync({
        body: {
          orgId,
          warehouseId: warehouseId!,
          appName: APP_NAME,
          name: values.name,
          zones: values.zones,
          users: values.users,
        },
      });
      logger.info("Created user group", { name: values.name });
      toast.success(t("userGroups.form.createSuccessToast"));
    } catch (error) {
      logger.warn("Failed to create user group", { error, name: values.name });
      toast.error(errorMessage(error, t("userGroups.form.saveFailedFallback")));
      throw error;
    }

    for (const userId of values.users) {
      await syncSelectorGroup(userId, values.name);
    }
    refetchGroups();
  };

  const saveGroup = async (
    group: AppUserGroup,
    patch: { zones?: string[]; users?: string[] },
  ) => {
    try {
      await updateMutation.mutateAsync({
        body: {
          orgId: group.orgId,
          warehouseId: group.warehouseId,
          appName: group.appName,
          name: group.name,
          zones: patch.zones ?? group.zones,
          users: patch.users ?? group.users,
        },
      });
      logger.info("Updated user group", { name: group.name });
      refetchGroups();
    } catch (error) {
      logger.warn("Failed to update user group", { error, name: group.name });
      toast.error(
        errorMessage(error, t("userGroups.edit.updateFailedFallback")),
      );
      throw error;
    }
  };

  /** Adds a selector to the group, then mirrors the membership onto their own settings (see `syncSelectorGroupSetting`). */
  const addSelectorToGroup = async (group: AppUserGroup, userId: string) => {
    try {
      await saveGroup(group, { users: [...group.users, userId] });
    } catch {
      return; // saveGroup already reported the failure.
    }
    await syncSelectorGroup(userId, group.name);
  };

  /** Removes a selector from the group, then clears the group off their own settings so they don't read stale. */
  const removeSelectorFromGroup = async (
    group: AppUserGroup,
    userId: string,
  ) => {
    try {
      await saveGroup(group, {
        users: group.users.filter((id) => id !== userId),
      });
    } catch {
      return;
    }
    await syncSelectorGroup(userId, null);
  };

  const closeDeleteConfirm = () => {
    if (removeMutation.isPending) return;
    setDeleteTarget(undefined);
  };

  const confirmDelete = () => {
    if (!deleteTarget || removeMutation.isPending) return;

    const target = deleteTarget;
    removeMutation
      .mutateAsync({
        body: {
          orgId: target.orgId,
          warehouseId: target.warehouseId,
          appName: target.appName,
          name: target.name,
        },
      })
      .then(() => {
        logger.info("Deleted user group", { name: target.name });
        toast.success(t("userGroups.deleteConfirm.successToast"));
        setDeleteTarget(undefined);
        if (openName === target.name) closeOpen();
        refetchGroups();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to delete user group", {
          error,
          name: target.name,
        });
        toast.error(
          errorMessage(error, t("userGroups.deleteConfirm.errorToast")),
        );
      });
  };

  const linkParams = {
    lang: params.lang,
    org: params.org,
    location: params.location,
  };

  return (
    <div className={palletOverrides.basicPageContent}>
      <div className={styles.page}>
        <header className={styles.header}>
          <h1>{t("userGroups.heading")}</h1>
          <p className={styles.subtitle}>{t("userGroups.subheading")}</p>
        </header>

        {isLoading ? (
          <Loader type="page" />
        ) : isError ? (
          <AppError
            description={t("userGroups.failed")}
            resolution={t("userGroups.failureSupport")}
          />
        ) : (
          <>
            <div className={styles.statsRow}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>
                  {t("userGroups.stats.groupsDefined")}
                </span>
                <span className={styles.statValue}>{groups.length}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>
                  {t("userGroups.stats.selectorsInGroup")}
                </span>
                <span className={styles.statValue}>{assignedUserIds.size}</span>
              </div>
              <div className={`${styles.statCard} ${styles.statCardWarning}`}>
                <span className={styles.statLabel}>
                  {t("userGroups.stats.notYetAssigned")}
                </span>
                <span
                  className={`${styles.statValue} ${styles.statValueWarning}`}
                >
                  {unassignedSelectors.length}
                </span>
                <span className={styles.statNote}>
                  {t("userGroups.stats.notYetAssignedNote")} ·{" "}
                  <Link
                    className={styles.statLink}
                    to="/$lang/orgs/$org/outbound/whs/$location/selectors"
                    params={linkParams}
                  >
                    {t("userGroups.stats.viewSelectors")} →
                  </Link>
                </span>
              </div>
            </div>

            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitleRow}>
                <h2 className={styles.sectionTitle}>
                  {t("userGroups.listHeading")}
                </h2>
                <span className={styles.countBadge}>{groups.length}</span>
              </div>
              <Button
                className={styles.createButton}
                icon={{ type: "plus" }}
                text={t("userGroups.createButton")}
                onPress={() => setCreateOpen(true)}
                isDisabled={openMode === "edit" && openName !== undefined}
              />
            </div>

            {createOpen ? (
              <CreateGroupPanel
                allZoneNames={allZoneNames}
                zoneColor={zoneColor}
                unassignedSelectors={unassignedSelectors}
                onSubmit={handleCreateSubmit}
                onClose={() => setCreateOpen(false)}
                t={t}
              />
            ) : null}

            {groups.length === 0 ? (
              <EmptyState heading={t("userGroups.empty")} />
            ) : (
              <div className={styles.groupsList}>
                {groups.map((group) => (
                  <GroupRow
                    key={group.name}
                    group={group}
                    allZoneNames={allZoneNames}
                    zoneColor={zoneColor}
                    selectorById={selectorById}
                    unassignedSelectors={unassignedSelectors}
                    isOpen={openName === group.name}
                    mode={openMode}
                    onToggleView={() => toggleView(group.name)}
                    onToggleEdit={() => toggleEdit(group.name)}
                    onChangeZones={(nextZones) =>
                      saveGroup(group, { zones: nextZones })
                    }
                    onAddSelector={(userId) =>
                      addSelectorToGroup(group, userId)
                    }
                    onRemoveSelector={(userId) =>
                      removeSelectorFromGroup(group, userId)
                    }
                    onDelete={() => setDeleteTarget(group)}
                    t={t}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <DeleteGroupConfirm
        group={deleteTarget}
        isPending={removeMutation.isPending}
        onCancel={closeDeleteConfirm}
        onConfirm={confirmDelete}
        t={t}
      />
    </div>
  );
}
